import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Loader2 } from "lucide-react";
import api from "../lib/api";
import { gaEvent } from "../lib/ga";

const SAMPLES = [
  "14700 Big Basin Way, Saratoga, CA 95070",
  "10123 N Wolfe Rd, Cupertino, CA 95014",
  "1601 Willow St, San Jose, CA 95125",
  "455 University Ave, Palo Alto, CA 94301",
];

export default function AddressSearch({ large = false, cityContext = null, chips = null, placeholder = "Enter your property address" }) {
  const [address, setAddress] = useState("");
  const [error, setError] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loadingSug, setLoadingSug] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const nav = useNavigate();
  const debRef = useRef(null);
  const boxRef = useRef(null);
  const pickedRef = useRef(false);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (pickedRef.current) { pickedRef.current = false; return; }
    clearTimeout(debRef.current);
    const q = address.trim();
    if (q.length < 4) { setSuggestions([]); setOpen(false); return; }
    setLoadingSug(true);
    debRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/property/autocomplete", { params: { q } });
        setSuggestions(data.suggestions || []);
        setOpen((data.suggestions || []).length > 0);
        setActiveIdx(-1);
      } catch { setSuggestions([]); }
      finally { setLoadingSug(false); }
    }, 450);
    return () => clearTimeout(debRef.current);
  }, [address]);

  const go = (addr) => {
    let a = addr.trim();
    if (a.length < 4) { setError("Please enter a valid Santa Clara or San Mateo County address."); return; }
    if (cityContext && !a.toLowerCase().includes(cityContext.toLowerCase())) a = `${a}, ${cityContext}, CA`;
    gaEvent("generate_estimate", { address: a, city_context: cityContext || "" });
    const q = new URLSearchParams({ address: a });
    nav(`/valuation?${q.toString()}`);
  };

  const pick = (s) => {
    pickedRef.current = true;
    setAddress(s.label);
    setSuggestions([]);
    setOpen(false);
    go(s.label);
  };

  const submit = (e) => { e?.preventDefault(); setOpen(false); go(address); };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && activeIdx >= 0) { e.preventDefault(); pick(suggestions[activeIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="w-full" ref={boxRef}>
      <form onSubmit={submit} data-testid="address-search-form"
        className={`relative flex flex-col sm:flex-row gap-2 rounded-2xl bg-white p-2 shadow-[0_12px_40px_rgba(27,59,43,0.10)] border border-brand-hairline`}>
        <div className="relative flex flex-1 items-center gap-3 px-4">
          <MapPin className="h-5 w-5 text-brand-gold shrink-0" />
          <input
            data-testid="address-search-input"
            value={address}
            onChange={(e) => { setAddress(e.target.value); setError(""); }}
            onKeyDown={onKeyDown}
            onFocus={() => { if (suggestions.length) setOpen(true); }}
            placeholder={placeholder}
            aria-label="Property address"
            autoComplete="off"
            className={`w-full bg-transparent outline-none placeholder:text-brand-stone/60 text-brand-ink ${large ? "py-4 text-base sm:text-lg" : "py-3 text-base"}`}
          />
          {loadingSug && <Loader2 className="h-4 w-4 animate-spin text-brand-stone/60 shrink-0" />}

          {open && suggestions.length > 0 && (
            <ul data-testid="address-suggestions"
              className="absolute left-0 right-0 top-full z-30 mt-2 max-h-72 overflow-auto rounded-xl border border-brand-hairline bg-white py-1 shadow-[0_16px_48px_rgba(27,59,43,0.16)]">
              {suggestions.map((s, i) => (
                <li key={`${s.label}-${i}`}>
                  <button type="button" data-testid={`address-suggestion-${i}`}
                    onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                    onMouseEnter={() => setActiveIdx(i)}
                    className={`flex w-full items-start gap-3 px-4 py-2.5 text-left text-sm transition-colors ${i === activeIdx ? "bg-brand-emeraldLight text-brand-emerald" : "text-brand-ink hover:bg-brand-cream"}`}>
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" />
                    <span>{s.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          type="submit"
          data-testid="address-search-submit-button"
          className="flex items-center justify-center gap-2 rounded-xl bg-brand-emerald px-7 py-3.5 font-semibold text-brand-cream hover:bg-brand-emeraldHover transition-colors">
          <Search className="h-4 w-4" /> Get Value
        </button>
      </form>
      {error && <p className="mt-2 pl-2 text-sm text-destructive" data-testid="address-search-error">{error}</p>}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-xs text-brand-stone">Try:</span>
        {chips && chips.length ? (
          chips.map((c) => (
            <button key={c} type="button" onClick={() => { pickedRef.current = true; setAddress(cityContext ? `${c}, ${cityContext}, CA` : c); }} data-testid="neighborhood-chip"
              className="rounded-full border border-brand-hairline bg-white/60 px-3 py-1 text-xs text-brand-stone hover:border-brand-emerald hover:text-brand-emerald transition-colors">
              {c}
            </button>
          ))
        ) : (
          SAMPLES.map((s) => (
            <button key={s} onClick={() => { pickedRef.current = true; setAddress(s); }} data-testid="sample-address-chip"
              className="rounded-full border border-brand-hairline bg-white/60 px-3 py-1 text-xs text-brand-stone hover:border-brand-emerald hover:text-brand-emerald transition-colors">
              {s.split(",")[1]?.trim()}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
